<template>
  <tr
    class="even:bg-gray-100 cursor-pointer odd:bg-white child:p-2 first-child:pl-2 last-child:pr-2 first-child:border-l last-child:border-r child:border-gray-300 last:border-b"
    @click="showAttachments = !showAttachments"
  >
    <td class="w-80 text-wrap">
      <div class="flex space-x-1 items-center">
        <div
          :class="[{'rotate-90': showAttachments}]"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="btn-icon h-5 w-5 group-hover:text-gray-700">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </div>

        <div
          v-if="badge"
          class="py-1 px-2 rounded-md text-sm font-semibold"
          :class="[badge.color, badge.background]"
        >
          {{ badge.text }}
        </div>

        <p>
          {{ correspondence.description }}
        </p>
      </div>
    </td>

    <td>
      {{ correspondence.to }}
    </td>

    <td>
      {{ correspondence.from }}
    </td>

    <td>
      {{ correspondence.is_issued ? $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.CorrespondenceRow.issued') : $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.CorrespondenceRow.received') }}
    </td>

    <td>
      {{ correspondence.dateReceived.toLocaleString(DateTime.DATE_SHORT) }}
    </td>

    <td>
      {{ correspondence.response_required ? $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.CorrespondenceRow.yes') : $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.CorrespondenceRow.no') }}
    </td>
  </tr>

  <tr
    v-if="showAttachments"
    class="bg-gray-100 child:border-x child:border-gray-300 child:border-b"
  >
    <td colspan="100%">
      <div
        v-if="correspondenceMedia.mediaRequestAction.isNot(States.LOADING)"
        class="relative h-full"
      >
        <p
          v-if="correspondenceMedia.mediaCollection.value.length === 0"
          class="font-semibold p-10 my-auto text-center"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.CorrespondenceRow.noAttachmentsFound') }}
        </p>

        <button
          v-if="correspondenceMedia.mediaCollection.value.length > 1"
          class="absolute left-2 bottom-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
          type="button"
          @click="mediaSwiper.slidePrev()"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <Swiper
          class="max-w-sm"
          :space-between="20"
          :loop="correspondenceMedia.mediaCollection.value.length > 1"
          centered-slides
          :modules="[Pagination]"
          :pagination="{
            el: `.swiper-pagination-correspondence-${correspondence.id}`,
            type: 'bullets'
          }"
          @swiper="setSwiper"
        >
          <SwiperSlide
            v-for="media in correspondenceMedia.mediaCollection.value"
            :key="media.uuid"
            class="!h-auto"
          >
            <div class="flex items-center w-full justify-center h-full">
              <MediaDisplay
                class="max-w-60"
                :media="media"
                @delete="correspondenceMedia.removeMedia(media.uuid)"
                @refresh="correspondenceMedia.refreshMedia(media.uuid)"
              />
            </div>
          </SwiperSlide>
        </Swiper>

        <div class="flex mx-auto w-fit">
          <div :class="'swiper-pagination-correspondence-' + correspondence.id" />
        </div>

        <button
          v-if="correspondenceMedia.mediaCollection.value.length > 1"
          class="absolute right-2 bottom-1/2 border border-black rounded-full w-8 h-8 flex justify-center items-center hover:bg-gray-100 active:bg-gray-200 z-10 bg-white"
          type="button"
          @click="mediaSwiper.slideNext()"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>
      </div>

      <div
        v-else
        class="flex w-full"
      >
        <AppSpinner class="mx-auto" />
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import MediaDisplay from '@/components/MediaDisplay.vue';
import { useMediaCollection } from '@/composables/useMediaCollection';
import { States } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import Correspondence from '@/models/Correspondence';
import { useCorrespondencesStore } from '@/store/correspondences';
import { DateTime } from 'luxon';
import { Pagination } from 'swiper/modules';
import { Swiper as SwiperInterface } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  correspondence: Correspondence;
}>();

const showAttachments = ref(false);
const correspondencesStoreStore = useCorrespondencesStore();
const correspondenceMedia = useMediaCollection(correspondencesStoreStore.fetchMedia);

watch(showAttachments, (newValue) => {
  if(newValue) {
    correspondenceMedia.clear();
    correspondenceMedia.fetchMedia(props.correspondence.id);
  }
});

const mediaSwiper = ref<SwiperInterface>();

const setSwiper = (swiper: SwiperInterface) => {
  mediaSwiper.value = swiper;
};

const badge = computed(() => {
  if(props.correspondence.type === 'notice-of-variation') {
    return {
      color: 'text-white',
      background: 'bg-ccm-blue-500',
      text: t('Models.Correspondence.type.notice-of-variation'),
    };
  } else if(props.correspondence.type === 'variation-price-submission') {
    return {
      color: 'text-white',
      background: 'bg-ccm-blue-500',
      text: t('Models.Correspondence.type.variation-price-submission'),
    };
  } else if(props.correspondence.type === 'notice-of-pricing') {
    return {
      color: 'text-white',
      background: 'bg-ccm-blue-500',
      text: t('Models.Correspondence.type.notice-of-pricing'),
    };
  } else if(props.correspondence.type === 'approval-of-variation') {
    return {
      color: 'text-white',
      background: 'bg-ccm-blue-500',
      text: t('Models.Correspondence.type.approval-of-variation'),
    };
  } else if(props.correspondence.type === 'variation-provision-declined') {
    return {
      color: 'text-white',
      background: 'bg-orange-500',
      text: t('Models.Correspondence.type.variation-provision-declined'),
    };
  }

  return undefined;
});
</script>

<style scoped></style>
